import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

// Create a global axios instance for authenticated requests
export const authApiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

let isSessionExpiredShown = false; // Prevent multiple alerts

export const useAuthInterceptor = () => {
  const { logout, getAccessTokenSilently } = useAuth0();
  const { showSessionExpired } = useToast();

  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = authApiClient.interceptors.request.use(
      async (config) => {
        try {
          const token = await getAccessTokenSilently();
          config.headers.Authorization = `Bearer ${token}`;
          config.headers['Content-Type'] = 'application/json';
          config.credentials = 'include';
        } catch (error) {
          console.warn('Failed to get access token:', error);
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor to handle auth errors
    const responseInterceptor = authApiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { response } = error;

        // Check for authentication/authorization errors
        if (response && (response.status === 401 || response.status === 403)) {
          // Prevent multiple session expired toasts
          if (!isSessionExpiredShown) {
            isSessionExpiredShown = true;

            // Show session expired toast with logout callback
            showSessionExpired(() => {
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                  federated: true,
                },
              });
            });
          }
        }

        return Promise.reject(error);
      },
    );

    // Cleanup interceptors on unmount
    return () => {
      authApiClient.interceptors.request.eject(requestInterceptor);
      authApiClient.interceptors.response.eject(responseInterceptor);
      isSessionExpiredShown = false; // Reset flag
    };
  }, [logout, getAccessTokenSilently, showSessionExpired]);

  return authApiClient;
};
